import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { loadActionPlanStatusDetails } from '../../../store/directusService';
import { updatePreSelects, updateSelectedItems } from '../../../utils/directusFunctions';
import { NO_CONTRACT_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import { ACTION_PLAN_DETAILS_ERROR } from '../../../utils/formValidation/loadingErrorMessageConstants';
import { hasRole, LOCAL_ADMIN, SUPERUSER } from '../../../utils/userRoles';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import NotFound from '../../notFound/NotFound';
import LoadingSpinner from '../../ui/LoadingSpinner';

import { validate } from './validateActionPlansAdmin';

import form from '../../../commonStyles/formStyles.module.css';

const acceptedRoles = [SUPERUSER];
const viewRoles = [LOCAL_ADMIN, SUPERUSER];
const _ACTION_PLAN_NAME = 'action_plan_status';
const _MESSAGE = 'Updating action_plan_status collection';

const ActionPlansAdmin = ({ contractId }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const [selectedActionPlanStatusIds, setSelectedActionPlanStatusIds] = useState([]);
    const [preSelectedActionPlanStatuses, setPreSelectedActionPlanStatuses] = useState([]);
    const [preSelectedActionPlanStatusIds, setPreSelectedActionPlanStatusIds] = useState([]);
    const [errors, setErrors] = useState({});
    const [isDisabled, setIsDisabled] = useState(true);
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const roles = useSelector((state) => state.entities.userService.loggedInUser.roles);
    const actionPlanStatusDetails = useSelector(
        (state) => state.entities.directusService.actionPlanStatusDetails
    );
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        if (contractId) dispatch(loadActionPlanStatusDetails());
    }, [contractId]);

    useEffect(() => {
        setIsDisabled(!hasRole(acceptedRoles, roles));
    }, [roles]);

    useEffect(() => {
        if (actionPlanStatusDetails?.length) {
            const { preSelects, preSelectIds } = updatePreSelects(
                _ACTION_PLAN_NAME,
                actionPlanStatusDetails,
                contractId
            );
            setPreSelectedActionPlanStatuses(preSelects);
            setPreSelectedActionPlanStatusIds(preSelectIds);
        }
    }, [actionPlanStatusDetails]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    useEffect(() => {
        if (successMessage.includes(_MESSAGE)) dispatch(loadActionPlanStatusDetails());
        setIsButtonDisabled(false);
    }, [successMessage]);

    // FNS
    const loadingError = () => {
        if (!contractId) return NO_CONTRACT_ERROR;
        if (actionPlanStatusDetails?.length < 1) return ACTION_PLAN_DETAILS_ERROR;
    };

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    // EVENT HANDLERS
    const onActionPlanStatusChange = (chosenIds) => {
        clearError('actionPlanStatusIds');
        setSelectedActionPlanStatusIds(chosenIds);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        setIsButtonDisabled(true);
        const newErrors = validate(preSelectedActionPlanStatusIds, selectedActionPlanStatusIds);
        if (Object.keys(newErrors)?.length) {
            setErrors(newErrors);
            return;
        }

        updateSelectedItems(
            _ACTION_PLAN_NAME,
            actionPlanStatusDetails,
            selectedActionPlanStatusIds,
            contractId,
            _ACTION_PLAN_NAME,
            dispatch
        );
    };

    // RENDER
    if (!hasRole(viewRoles, roles)) return <NotFound />;
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    return (
        <form
            className={form.formWrapper}
            onSubmit={onSubmit}
            data-testid="action_plan_admin_form_start">
            <MultiSelect
                id="actionPlanStatusIds"
                label="Action Plan Outcomes"
                placeholder="Select action plan outcomes..."
                disabled={isDisabled}
                mandatory={true}
                menuItems={actionPlanStatusDetails || []}
                preSelectedIds={preSelectedActionPlanStatusIds}
                preSelects={preSelectedActionPlanStatuses}
                error={errors.actionPlanStatusIds}
                onChange={(chosenIds) => onActionPlanStatusChange(chosenIds)}
            />

            <Button
                id="actionPlanAdminButton"
                content="UPDATE OUTCOMES"
                disabled={isDisabled || isButtonDisabled}
                customErrorClass="reduceTopMargin"
                error={errors.button}
                clearError={() => clearError('button')}
            />
        </form>
    );
};

ActionPlansAdmin.propTypes = {
    contractId: PropTypes.string
};

export default ActionPlansAdmin;
